// LLM chat assistant client

"use strict";

import { ApiLlm } from "@/api/api-group-llm";
import { LlmChatMessageToolCall, LLmSidecarClientMessage, LLmSidecarServerMessage, LLmTool } from "@/utils/llm-sidecar-messages";
import { Timeouts } from "@/utils/timeout";
import { getUniqueStringId } from "@/utils/unique-id";
import { Request } from "@asanrom/request-browser";
import { AppEvents } from "./app-events";
import type { RulebookReviewReport } from "@/utils/rulebook-review";

// State to display to the user
export type SidecarLLmChatAssistantEventHandlerState = "loading" | "error" | "idle" | "generating" | "running-tools" | "reconnecting";

/**
 * A handler for events of LLmChatAssistant
 */
export interface SidecarLLmChatAssistantEventHandler {
    /**
     * Assistant state changed
     * @param state The state
     */
    onStateChanged(state: SidecarLLmChatAssistantEventHandlerState): void;

    /**
     * An error occurred. Display it to the user.
     * @param errorCode The error code
     * @param errorMessage The error message
     */
    onError(errorCode: string, errorMessage: string): void;

    /**
     * The LLM responds. This function will be called multiple times chunk by chunk
     * as the response is generated.
     * @param id Message ID
     * @param chunk Message chunk
     */
    onMessage(id: string, chunk: string): void;

    /**
     * The connection had been re-established before an error occurred
     */
    onReconnected(): void;
}

// Internal status of the chat assistant client
type SidecarLLmChatAssistantStatus = "loading" | "connecting" | "connected" | "ready";

/**
 * Implemented LLM tool
 */
export type LLmImplementedTool = LLmTool & {
    invoke: (toolCall: LlmChatMessageToolCall, abortController: AbortController) => Promise<string>;
};

// Interval to send heartbeat messages (milliseconds)
const HEARTBEAT_INTERVAL = 20 * 1000;

// Background retry interval following a fatal error (milliseconds)
const BACKGROUND_RETRY_INTERVAL = 2 * 1000;

// Heartbeat deadline (milliseconds)
const HEARTBEAT_DEADLINE = 60 * 1000;

/**
 * Chat assistant using the sidecar LLM
 */
export class SidecarLLmChatAssistant {
    // Event handler
    private eventHandler: SidecarLLmChatAssistantEventHandler;

    // List of LLM tools
    private tools: LLmImplementedTool[];

    // Context ID for the LLM
    private contextId: string;

    // Extra context for the LLM
    private extraContext: string;
    private disableRag: boolean;

    // Status
    private status: SidecarLLmChatAssistantStatus;

    // Auth changed listener
    private authChangedListener: () => void;

    // Request ID
    private requestId: string;

    // Websocket URL
    private wsUrl: string;

    // Authentication token
    private authToken: string;

    // WebSocket
    private socket: WebSocket | null;

    // Interval to send periodic heartbeat messages
    private heartbeatInterval: null | ReturnType<typeof setInterval>;

    // Timestamp of the last server heartbeat
    private lastServerHeartbeat: number;

    // Message ID
    private messageId: string;

    // Pending tool calls
    private pendingToolCalls: LlmChatMessageToolCall[];

    // Pending cancel?
    private pendingCancel: boolean;

    // Object to cancel running tools
    private toolCancellable: {
        cancelled: boolean;
        abortControllers: AbortController[];
    } | null;

    private retryAttempts = 0;
    private maxSilentRetries = 5;
    private retryInterval = 2000;

    private isBackgroundRetrying = false;

    /**
     * Constructor
     * @param eventHandler The event handler
     */
    constructor(eventHandler: SidecarLLmChatAssistantEventHandler, tools: LLmImplementedTool[]) {
        this.eventHandler = eventHandler;
        this.tools = tools;
        this.contextId = "";
        this.extraContext = "";
        this.disableRag = false;

        this.requestId = getUniqueStringId();

        this.status = "loading";

        this.wsUrl = "";
        this.authToken = "";

        this.socket = null;

        this.heartbeatInterval = null;

        this.lastServerHeartbeat = 0;

        this.messageId = getUniqueStringId();

        this.pendingToolCalls = [];

        this.pendingCancel = false;

        this.toolCancellable = null;

        this.authChangedListener = () => {
            if (this.status === "loading") {
                this.load();
            }
        };

        AppEvents.AddEventListener("auth-status-changed", this.authChangedListener);

        this.load();
    }

    /**
     * Handle any connection errors (HTTP or WebSocket)
     * @param errorCode The error code
     * @param errorMessage The error message
     */
    private handleConnectionFailure(errorCode: string, errorMessage: string) {
        this.retryAttempts++;

        if (this.retryAttempts > this.maxSilentRetries) {
            this.status = "loading";

            if (!this.isBackgroundRetrying) {
                this.isBackgroundRetrying = true;
                this.eventHandler.onError(errorCode, errorMessage);
                this.eventHandler.onStateChanged("error");
            }

            Timeouts.Set(this.requestId, BACKGROUND_RETRY_INTERVAL, () => {
                this.load();
            });
        } else {
            this.status = "connecting";
            this.eventHandler.onStateChanged("reconnecting");

            Timeouts.Set(this.requestId, this.retryInterval, () => {
                this.load();
            });
        }
    }

    /**
     * Loads the websocket URL and auth token from the API
     */
    private load() {
        this.closeSocket();

        Timeouts.Abort(this.requestId);

        if (this.isBackgroundRetrying) {
            this.status = "connecting";
        } else if (this.retryAttempts > 0) {
            this.status = "connecting";
            this.eventHandler.onStateChanged("reconnecting");
        } else {
            this.status = "loading";
            this.eventHandler.onStateChanged("loading");
        }

        Request.Pending(this.requestId, ApiLlm.GetChatAssistantConnectionDetails())
            .onSuccess((res) => {
                this.wsUrl = res.url;
                this.authToken = res.authToken;

                this.connect();
            })
            .onRequestError((err, handleErr) => {
                handleErr(err, {
                    unauthorized: () => {
                        AppEvents.Emit("unauthorized");
                    },
                    forbidden: () => {
                        this.handleConnectionFailure("ACCESS_DENIED", "The LLM service is not currently available.");
                    },
                    notFound: () => {
                        this.handleConnectionFailure("LLM_NOT_AVAILABLE", "The LLM service is not currently available.");
                    },
                    temporalError: () => {
                        this.handleConnectionFailure("LLM_NOT_AVAILABLE", "Could not connect to the server. Retrying...");
                    },
                });
            })
            .onUnexpectedError((err) => {
                this.handleConnectionFailure("UNEXPECTED_ERROR", "Error: " + err.message);
            });
    }

    /**
     * Connects to the websocket
     */
    private connect() {
        Request.Abort(this.requestId);
        Timeouts.Abort(this.requestId);
        this.closeSocket();

        this.status = "connecting";

        if (!this.isBackgroundRetrying) {
            this.eventHandler.onStateChanged(this.retryAttempts > 0 ? "reconnecting" : "loading");
        }

        this.socket = new WebSocket(this.wsUrl);

        this.socket.onclose = () => {
            this.clearHeartbeatInterval();
            this.cancelTools();

            this.socket = null;

            this.handleConnectionFailure("LLM_NOT_AVAILABLE", "The LLM service is not currently available.");
        };

        this.socket.onopen = () => {
            this.status = "connected";
            this.pendingCancel = false;

            this.retryAttempts = 0;

            this.setupHeartbeatInterval();
            this.authenticate();
        };

        this.socket.onerror = (ev) => {
            console.error("Websocket error:", ev);
        };

        this.socket.onmessage = (ev) => {
            this.lastServerHeartbeat = Date.now();

            const message = ev.data;

            if (typeof message !== "string") {
                return;
            }

            let parsedMessage: LLmSidecarServerMessage;

            try {
                parsedMessage = JSON.parse(message);
            } catch (ex) {
                console.error(ex);
                return;
            }

            this.handleMessage(parsedMessage);
        };
    }

    /**
     * Closes the websocket connection
     */
    private close() {
        if (this.socket) {
            this.socket.close();
        }
    }

    /**
     * Handles server message
     * @param msg The message
     */
    private handleMessage(msg: LLmSidecarServerMessage) {
        switch (msg.type) {
            case "auth-ack":
                {
                    this.sendLLmContext();
                    this.sendLLmTools();
                    this.status = "ready";

                    if (this.isBackgroundRetrying) {
                        this.isBackgroundRetrying = false;
                        this.eventHandler.onReconnected();
                    }

                    this.eventHandler.onStateChanged("idle");
                }
                break;
            case "auth-error":
                {
                    this.status = "loading";
                    this.eventHandler.onError("AUTH_ERROR", "Authentication error: " + msg.error);
                    this.close();
                }
                break;
            case "error":
                {
                    this.eventHandler.onError(msg.errorCode, msg.errorMessage);
                }
                break;
            case "llm-start":
                {
                    this.cancelTools();
                    this.messageId = getUniqueStringId();
                    this.pendingCancel = false;
                    this.eventHandler.onStateChanged("generating");
                }
                break;
            case "llm-chunk":
                {
                    const chunk = msg.content;

                    if (chunk && !this.pendingCancel) {
                        this.eventHandler.onMessage(this.messageId, chunk);
                    }

                    if (msg.toolCalls && msg.toolCalls.length > 0) {
                        for (const toolCall of msg.toolCalls) {
                            this.pendingToolCalls.push(toolCall);
                        }
                    }
                }
                break;
            case "llm-finish":
                {
                    this.eventHandler.onStateChanged("idle");

                    if (!this.pendingCancel) {
                        this.runPendingTools();
                    } else {
                        this.pendingCancel = false;
                        this.pendingToolCalls = [];
                    }
                }
                break;
        }
    }

    /**
     * Sends a message if connected
     * @param msg The message
     */
    private send(msg: LLmSidecarClientMessage) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(msg));
        }
    }

    /**
     * Sets up heartbeat interval
     */
    private setupHeartbeatInterval() {
        this.clearHeartbeatInterval();
        this.lastServerHeartbeat = Date.now();

        this.heartbeatInterval = setInterval(() => {
            this.send({ type: "heartbeat" });

            if (Date.now() - this.lastServerHeartbeat > HEARTBEAT_DEADLINE) {
                this.close();
            }
        }, HEARTBEAT_INTERVAL);
    }

    /**
     * Clears heartbeat interval
     */
    private clearHeartbeatInterval() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Sends the authentication
     */
    private authenticate() {
        this.send({
            type: "auth",
            token: this.authToken,
        });
    }

    /**
     * Sends LLM context
     */
    private sendLLmContext() {
        this.send({
            type: "context",
            contextId: this.contextId,
            extraContext: this.extraContext,
            disableRag: this.disableRag,
        });
    }

    /**
     * Sends LLM tools
     */
    private sendLLmTools() {
        this.send({
            type: "tools",
            tools: this.tools,
        });
    }

    /**
     * Closes the socket and strips it of event handlers
     */
    private closeSocket() {
        if (this.socket) {
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.onopen = null;
            this.socket.onmessage = null;
            try {
                this.socket.close();
            } catch (ex) {
                console.error(ex);
            }
            this.socket = null;
        }
    }

    /**
     * Runs a tool and returns the response
     * @param toolCall The tool call
     * @param abortController The abort controller
     * @returns The text response
     */
    private async runTool(toolCall: LlmChatMessageToolCall, abortController: AbortController): Promise<{ id: string; result: string }> {
        const foundTool = this.tools.find((tool) => tool.name.toLowerCase() === toolCall.name.toLowerCase());

        if (!foundTool) {
            return { id: toolCall.id, result: `Tool with name '${toolCall.name}' was not found.` };
        }

        const toolName = foundTool.name;

        let toolResponse: string;

        try {
            toolResponse = await foundTool.invoke(toolCall, abortController);
        } catch (ex) {
            return { id: toolCall.id, result: `Tool with name '${toolName}' finished with an error: ${ex.message}` };
        }

        return { id: toolCall.id, result: `Response from tool '${toolName}':\n\n ${toolResponse}` };
    }

    /**
     * Runs tools in sequence
     * @param toolCalls The list of tool calls
     * @param abortController The abort controller
     * @returns
     */
    private async runTools(
        toolCalls: LlmChatMessageToolCall[],
        abortController: AbortController,
    ): Promise<{ id: string; result: string }[]> {
        const results: { id: string; result: string }[] = [];

        for (const toolCall of toolCalls) {
            if (abortController.signal.aborted) {
                return [];
            }

            const toolResult = await this.runTool(toolCall, abortController);

            results.push(toolResult);
        }

        return results;
    }

    /**
     * Runs the pending tools
     */
    private runPendingTools() {
        this.cancelTools();

        if (this.pendingToolCalls.length === 0) {
            return;
        }

        const cancellable = {
            cancelled: false,
            abortControllers: [] as AbortController[],
        };

        this.toolCancellable = cancellable;

        const toolCalls = this.pendingToolCalls;
        this.pendingToolCalls = [];

        this.eventHandler.onStateChanged("running-tools");

        const abortController = new AbortController();
        cancellable.abortControllers.push(abortController);

        this.runTools(toolCalls, abortController).then((responses) => {
            if (cancellable.cancelled) {
                return;
            }

            this.toolCancellable = null;

            // Send the tool responses back to the LLM

            this.send({
                type: "tools-results",
                results: responses,
            });
        });
    }

    /**
     * Cancels any running tools
     */
    private cancelTools() {
        if (this.toolCancellable) {
            this.toolCancellable.cancelled = true;
            this.toolCancellable.abortControllers.forEach((ac) => {
                ac.abort();
            });
            this.toolCancellable = null;
            return;
        }
    }

    /**
     * Sets context for the LLM
     * @param contextId The context ID
     */
    public setContext(contextId: string, extraContext: string, disableRag?: boolean) {
        this.contextId = contextId;
        this.extraContext = extraContext;
        this.disableRag = !!disableRag;
        this.sendLLmContext();
    }

    /**
     * Sets the LLM tools
     * @param tools The list of tools
     */
    public setTools(tools: LLmImplementedTool[]) {
        this.tools = tools;
        this.sendLLmTools();
    }

    /**
     * Sends a chat message from the user
     * @param msg The message
     */
    public chat(msg: string) {
        this.send({
            type: "chat",
            message: msg,
        });
    }

    /**
     * Cancels the LLM generation
     */
    public cancel() {
        this.pendingCancel = true;
        this.send({
            type: "cancel",
        });
        this.cancelTools();
    }

    /**
     * Cancels the LLM generation
     * and clears the history
     */
    public clear() {
        this.pendingCancel = true;
        this.send({
            type: "clear",
        });
        this.cancelTools();
    }

    /**
     * Call to release any allocated resources and
     * close all connections
     */
    public dispose() {
        AppEvents.RemoveEventListener("auth-status-changed", this.authChangedListener);

        this.cancelTools();

        Request.Abort(this.requestId);
        Timeouts.Abort(this.requestId);

        this.clearHeartbeatInterval();

        this.closeSocket();
    }
}

/**
 * Chat assistant message
 */
export type SidecarLLmChatAssistantMessage = {
    /**
     * Message ID
     */
    id: string;

    /**
     * Message role
     */
    role: "user" | "assistant" | "error" | "success" | "rulebook-review";

    /**
     * Message content
     */
    content: string;

    /**
     * The report of a review against the rulebook, when the message is one.
     */
    rulebookReview?: RulebookReviewReport;
};

/**
 * Page LLM context
 */
export type PageLLmContext = {
    // Context ids
    contextIds?: string | string[];

    // Extra context
    extraContext?: string;

    // LLM tools
    tools?: LLmImplementedTool[];

    // True to ignore other page contexts
    ignorePageContext?: boolean;

    // True to leave out the fragments of the RAG while this context is set
    disableRag?: boolean;
};

// Stored page context
type StoredPageLLmContext = PageLLmContext & {
    // Unique ID
    id: string;

    // Priority (The higher priority the earlier in the prompt list)
    priority: number;
};

// Global chat assistant status
const GlobalChatAssistantStatus = {
    /**
     * Context list
     */
    context: [] as StoredPageLLmContext[],
};

/**
 * Get global LLM context
 * @returns The global LLM context
 */
export function globalLLmGetContext(): [contextId: string, extraContext: string, tools: LLmImplementedTool[], disableRag: boolean] {
    const contextIdList: string[] = [];
    const extraContextList: string[] = [];
    const tools: LLmImplementedTool[] = [];

    const contextIdSet = new Set<string>();
    const toolNameSet = new Set<string>();

    const ignoredContext = GlobalChatAssistantStatus.context.filter((c) => c.ignorePageContext);

    const fullContext = ignoredContext.length > 0 ? ignoredContext : GlobalChatAssistantStatus.context;

    fullContext.forEach((c) => {
        if (c.contextIds) {
            if (typeof c.contextIds === "string") {
                if (!contextIdSet.has(c.contextIds)) {
                    contextIdList.push(c.contextIds);
                    contextIdSet.add(c.contextIds);
                }
            } else {
                for (const id of c.contextIds) {
                    if (!id || contextIdSet.has(id)) {
                        continue;
                    }

                    contextIdList.push(id);
                    contextIdSet.add(id);
                }
            }
        }

        if (c.extraContext) {
            extraContextList.push(c.extraContext);
        }

        if (c.tools) {
            for (const tool of c.tools) {
                if (toolNameSet.has(tool.name)) {
                    continue;
                }

                tools.push(tool);
                toolNameSet.add(tool.name);
            }
        }
    });

    return [contextIdList.join("+"), extraContextList.join("\n\n"), tools, fullContext.some((c) => c.disableRag)];
}

/**
 * Adds a page context to the global LLM context
 * @param id The unique page ID (use generated unique IDs)
 * @param context The context
 * @param priority The priority (higher priority context gets put first in the context prompt)
 */
export function globalLLmAddPageContext(id: string, context: PageLLmContext, priority?: number) {
    GlobalChatAssistantStatus.context = GlobalChatAssistantStatus.context.filter((a) => a.id !== id);

    GlobalChatAssistantStatus.context.push({
        id,
        priority: priority || 0,
        contextIds: context.contextIds,
        extraContext: context.extraContext,
        ignorePageContext: context.ignorePageContext,
        tools: context.tools,
        disableRag: context.disableRag,
    });

    GlobalChatAssistantStatus.context = GlobalChatAssistantStatus.context.sort((a, b) => {
        if (a.priority > b.priority) {
            return -1;
        } else if (a.priority < b.priority) {
            return 1;
        } else if (a.id < b.id) {
            return -1;
        } else {
            return 1;
        }
    });

    AppEvents.Emit("llm-context-changed");
}

/**
 * Removes a page LLM context (usually after unmounts)
 * @param id The unique ID used to add the context
 */
export function globalLLmDeletePageContext(id: string) {
    GlobalChatAssistantStatus.context = GlobalChatAssistantStatus.context.filter((a) => a.id !== id);

    AppEvents.Emit("llm-context-changed");
}
